import { useEffect } from 'react';
import { useMutation as useMutationUrql } from 'urql';
import { MutationResponse } from '@/shared/types';
import type { AnyVariables, DocumentInput } from 'urql';

interface MutationActions<TData = any> {
  onCompleted?: (data: TData) => void;
  onError?: (err: any) => void;
}

const useMutation = <TData = any, TVariables extends AnyVariables = AnyVariables>(
  mutation: DocumentInput<TData, TVariables>,
  actions: MutationActions<TData> = {},
): MutationResponse<TData, TVariables> => {
  const { onCompleted, onError } = actions;
  const [{ fetching, data, error }, action] = useMutationUrql<TData, TVariables>(mutation);

  useEffect(() => {
    if (fetching) return;
    if (data && typeof data === 'object' && !fetching && onCompleted) {
      const dataKeys = Object.keys(data);

      const firstKey = dataKeys.length > 0 ? dataKeys[0] : null;
      const completedData = !!firstKey ? (data as any)[firstKey] : null;
      if (completedData && completedData !== null) onCompleted(data);
    }
    if (error && !fetching && onError) {
      onError(error);
    }
  }, [fetching, data, error, onCompleted, onError]);

  return { action, isLoading: fetching, data, error };
};

export default useMutation;
